import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Merge, Search } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../context/AuthContext';

const fehler = (e) => e?.response?.data?.detail || 'Fehler';

const KontoWahl = ({ label, wert, setWert, testid }) => {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setItems([]); return undefined; }
    const t = setTimeout(() => api.get('/admin/konten/suche', { params: { q } }).then((r) => setItems(r.data.items)).catch(() => setItems([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{label}</div>
      {wert ? (
        <div className="flex items-center gap-2 h-10 px-3 rounded-[10px] border border-[#b8893d]/50 bg-[#fbf7f0] text-sm">
          <span className="truncate flex-1 min-w-0 text-[#1B3A5C]">{wert.name} · {wert.email}</span>
          <button type="button" onClick={() => setWert(null)} className="text-xs text-gray-500 underline shrink-0" data-testid={`${testid}-aendern`}>ändern</button>
        </div>
      ) : (
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name oder E-Mail suchen …" className="w-full h-10 pl-9 pr-3 border border-gray-300 rounded-[10px] text-sm" data-testid={`${testid}-suche`} />
          {items.length > 0 && <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">{items.map((u) => (
            <li key={u.user_id}><button type="button" onClick={() => { setWert(u); setQ(''); }} className="w-full text-left px-3 py-2 text-sm hover:bg-[#f5f8fa]" data-testid={`${testid}-treffer-${u.user_id}`}>
              <span className="text-[#1B3A5C]">{u.name || '—'}</span> <span className="text-gray-500">· {u.email} · {u.role}</span></button></li>))}</ul>}
        </div>
      )}
    </div>
  );
};

const Konto = ({ k, titel }) => (
  <div className="text-xs text-gray-700 space-y-0.5">
    <div className="text-[10px] uppercase tracking-wider text-gray-500">{titel}</div>
    <div className="text-sm text-[#1B3A5C] font-medium">{k.name} · {k.email}</div>
    <div>Rolle {k.role} · {k.auth_provider === 'google' ? 'Google' : 'E-Mail'} · Passwort {k.passwort ? 'ja' : 'nein'} · 2FA {k.zwei_fa_aktiv ? 'an' : 'aus'}</div>
  </div>
);

export const KontenZusammenfuehren = () => {
  const [haupt, setHaupt] = useState(null);
  const [zweit, setZweit] = useState(null);
  const [email, setEmail] = useState('');
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prot, setProt] = useState([]);
  const ladeProt = useCallback(() => api.get('/admin/konten/zusammenfuehren/protokoll').then((r) => setProt(r.data.items)).catch(() => {}), []);
  useEffect(() => { ladeProt(); }, [ladeProt]);
  useEffect(() => {
    setV(null);
    if (!haupt || !zweit) return;
    setEmail(haupt.email);
    api.get('/admin/konten/zusammenfuehren/vorschau', { params: { haupt: haupt.user_id, zweit: zweit.user_id } }).then((r) => setV(r.data)).catch((e) => { toast.error(fehler(e)); setV(false); });
  }, [haupt, zweit]);
  const los = async () => {
    if (window.prompt(`Konto ${zweit.email} endgültig in ${haupt.name} aufgehen lassen? Zur Bestätigung „ZUSAMMENFÜHREN“ eingeben:`) !== 'ZUSAMMENFÜHREN') return;
    setBusy(true);
    try {
      await api.post('/admin/konten/zusammenfuehren', { haupt: haupt.user_id, zweit: zweit.user_id, email, bestaetigung: 'ZUSAMMENFÜHREN' });
      toast.success('Konten zusammengeführt – alle Sitzungen des Kontos wurden beendet');
      setHaupt(null); setZweit(null); ladeProt();
    } catch (e) { toast.error(fehler(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="konten-zusammenfuehren">
      <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
        <p className="text-sm text-[#34536b]">Das <b>Zweitkonto</b> geht vollständig im <b>Hauptkonto</b> auf: Akten-Zuständigkeiten, Eingang, Arbeitszeit, Urlaub, Vertretungen, Protokolle und Mandanten-Verknüpfungen werden übertragen. Die nicht gewählte Adresse bleibt als Google-Anmeldung nutzbar. Das Zweitkonto wird archiviert und entfernt.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <KontoWahl label="Hauptkonto (bleibt bestehen)" wert={haupt} setWert={setHaupt} testid="merge-haupt" />
          <KontoWahl label="Zweitkonto (geht auf)" wert={zweit} setWert={setZweit} testid="merge-zweit" />
        </div>
        {v === null && haupt && zweit && <Loader2 className="w-5 h-5 animate-spin text-[#b8893d]" />}
        {v && (
          <div className="space-y-4 border-t border-gray-100 pt-4" data-testid="merge-vorschau">
            <div className="grid md:grid-cols-2 gap-4"><Konto k={v.haupt} titel="Hauptkonto" /><Konto k={v.zweit} titel="Zweitkonto" /></div>
            <div><div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Übertragene Einträge je Bereich</div>
              <div className="flex flex-wrap gap-1.5" data-testid="merge-zaehler">{Object.keys(v.uebernommen).length ? Object.entries(v.uebernommen).map(([c, n]) => <span key={c} className="text-[11px] px-2 py-0.5 rounded-full bg-[#f5f8fa] border border-gray-200">{c}: <b>{n}</b></span>) : <span className="text-xs text-gray-500">keine</span>}</div></div>
            {v.warnungen?.length > 0 && <ul className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1" data-testid="merge-warnungen">{v.warnungen.map((w) => <li key={w}>{w}</li>)}</ul>}
            {v.felder.length > 0 && <p className="text-xs text-gray-600" data-testid="merge-felder">Ergänzte Profilangaben aus dem Zweitkonto: {v.felder.join(', ')}</p>}
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-[10px] uppercase tracking-wider text-gray-500">Haupt-Adresse (Login)
                <select value={email} onChange={(e) => setEmail(e.target.value)} className="block mt-1 h-10 px-3 border border-gray-300 rounded-[10px] text-sm bg-white normal-case tracking-normal" data-testid="merge-email">
                  {[v.haupt.email, v.zweit.email].map((x) => <option key={x} value={x}>{x}</option>)}
                </select></label>
              <button type="button" disabled={busy} onClick={los} className="inline-flex items-center gap-2 h-10 px-4 rounded-[10px] bg-[#1B3A5C] text-white text-sm disabled:opacity-50" data-testid="merge-ausfuehren"><Merge className="w-4 h-4" /> Konten zusammenführen</button>
            </div>
          </div>
        )}
      </div>
      {prot.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="merge-protokoll">
          <h3 className="text-sm font-medium text-[#1B3A5C] mb-2">Protokoll</h3>
          <ul className="text-xs text-gray-700 space-y-1">{prot.map((p) => <li key={p.am}>{new Date(p.am).toLocaleString('de-DE')} · {p.zweit_email} → {p.email} · {((n) => `${n} ${n === 1 ? 'Eintrag' : 'Einträge'}`)(Object.values(p.umgeschrieben || {}).reduce((a, b) => a + b, 0))} · {p.von}</li>)}</ul>
        </div>
      )}
    </div>
  );
};
